import { useState } from "react";
import { useForm, Link, router } from "@inertiajs/react";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SyncBranchModal } from "@/components/SyncBranchModal";
import { ArrowLeft, Clock } from "lucide-react";
import type { Business, PageProps, AvailabilityRule } from "@/types";
import { HoursDayList } from "@/components/HoursDayList";

interface BusinessFormProps extends PageProps {
  business: (Business & { google_maps_url?: string | null }) | null;
  errors?: Record<string, string>;
  availability?: AvailabilityRule[];
  has_other_businesses?: boolean;
}

export default function BusinessForm({
  business,
  errors = {},
  availability: initialAvailability = [],
  has_other_businesses = false,
  current_user,
  current_organization,
  current_business,
}: BusinessFormProps) {
  const orgSlug = current_organization?.slug || "";
  const isEditing = !!business;

  const [processing, setProcessing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [availability, setAvailability] = useState(initialAvailability);

  const { data, setData } = useForm({
    name: business?.name || "",
    address: business?.address || "",
    timezone: business?.timezone || "America/Mexico_City",
  });

  const submitForm = (syncAll = false) => {
    setProcessing(true);
    const formData: Record<string, unknown> = {
      ...data,
      ...(isEditing ? { availability, sync_all: syncAll } : {}),
    };

    if (isEditing) {
      router.put(`/${orgSlug}/businesses/${business.id}`, formData, {
        onFinish: () => setProcessing(false),
      });
    } else {
      router.post(`/${orgSlug}/businesses`, formData, {
        onFinish: () => setProcessing(false),
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing && has_other_businesses) {
      setShowSyncModal(true);
      return;
    }
    submitForm();
  };

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <Link
            href={`/${orgSlug}/businesses`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a sucursales
          </Link>
          <h1 className="text-2xl font-semibold text-foreground">
            {isEditing ? "Editar sucursal" : "Nueva sucursal"}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                placeholder="Ej: Sucursal Centro"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                required
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Link de Google Maps o dirección</Label>
              <Input
                id="address"
                placeholder="Ej: Av. Reforma 222, Col. Juárez, CDMX"
                value={data.address}
                onChange={(e) => setData("address", e.target.value)}
              />
              {errors.address && (
                <p className="text-sm text-destructive">{errors.address}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="timezone">Zona horaria</Label>
              <select
                id="timezone"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={data.timezone}
                onChange={(e) => setData("timezone", e.target.value)}
              >
                <option value="America/Mexico_City">Centro (CDMX, Monterrey, Guadalajara)</option>
                <option value="America/Mazatlan">Pacífico (Sinaloa, Nayarit, BCS)</option>
                <option value="America/Tijuana">Noroeste (Baja California)</option>
                <option value="America/Cancun">Sureste (Quintana Roo)</option>
                <option value="America/Hermosillo">Sonora</option>
              </select>
              {errors.timezone && (
                <p className="text-sm text-destructive">{errors.timezone}</p>
              )}
            </div>
          </div>

          {isEditing && availability.length > 0 && (
            <div className="rounded-xl border border-border bg-card">
              <div className="border-b border-border p-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <h2 className="font-medium">Horarios de atención</h2>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Horario predeterminado para esta sucursal.
                </p>
              </div>

              <HoursDayList
                availability={availability}
                onChange={setAvailability}
              />
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={processing}>
              {processing
                ? "Guardando..."
                : isEditing
                  ? "Guardar cambios"
                  : "Crear sucursal"}
            </Button>
            <Link href={`/${orgSlug}/businesses`}>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </Link>
          </div>
        </form>

        <SyncBranchModal
          open={showSyncModal}
          title="Actualizar horarios en todas las sucursales"
          description="¿Deseas aplicar estos horarios y datos en todas las sucursales?"
          confirmLabel="Actualizar en todas las sucursales"
          singleLabel="Solo en esta sucursal"
          onSyncAll={() => {
            setShowSyncModal(false);
            submitForm(true);
          }}
          onSingleOnly={() => {
            setShowSyncModal(false);
            submitForm(false);
          }}
          onCancel={() => setShowSyncModal(false)}
        />
      </div>
    </DashboardLayout>
  );
}
